import { FiUser } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import UserTopNavBar from "./UserTopNavBar";
import Learn from "./Learn";
import AskAi from "./AskAi";
import AppointmentPanel from "./AppointmentsPanel";
import Notifications from "./Notifications";
import RecordsPanel from "./RecordsPanel";
import UserProfile from "./UserProfile";
import ContentPersonalizationModal from "../../components/usercomponents/ContentPersonalizationModal";

import "./usercss/Home.css";

// Inline toast-style modal (matches AdminChangeVerification / AdminPage pattern).
// Auto-dismisses after 4 seconds so the user doesn't have to click anything.
function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      className={`feedback-modal ${type}`}
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        padding: "12px 20px",
        borderRadius: "8px",
        color: "#fff",
        backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
        zIndex: 9999,
        transition: "opacity 0.3s",
        whiteSpace: "pre-line",
      }}
    >
      {message}
    </div>
  );
}

function UserHome() {
  const navigate = useNavigate();
  const location = useLocation();

  // Safely get user data from location state or localStorage.
  // When arriving after signup/OTP, location.state has the full user; sync to localStorage
  // so Health Information and other sections display immediately without re-login.
  const getUserData = () => {
    if (location.state?.user) {
      const user = location.state.user;
      try {
        localStorage.setItem("user", JSON.stringify(user));
      } catch (e) {
        console.warn("Could not persist user to localStorage", e);
      }
      return user;
    }

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (err) {
        console.error("Failed to parse stored user data:", err);
        return null;
      }
    }

    return null;
  };

  const userData = getUserData();
  // Get firstName and lastName from localStorage since they're not stored in Firestore
  const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
  const firstName = storedDraft.firstName || '';
  const lastName = storedDraft.lastName || '';
  const fullName = userData ? `${firstName} ${lastName}`.trim() || userData.uniqueIdentifierCode || "User" : "User";

  // Show personalization modal only after registration (when coming from signup/OTP), not after login
  const [showPersonalizationModal, setShowPersonalizationModal] = useState(
    location.state?.fromSignup === true
  );

  // OAuth-result toast notification (populated when redirected back from
  // /appointments callback route after Google Calendar connect flow).
  const [notification, setNotification] = useState({ message: "", type: "" });
  const closeNotification = useCallback(() => {
    setNotification({ message: "", type: "" });
  }, []);

  // When arriving from the Google OAuth redirect via /appointments -> /user-home,
  // surface the success/error toast and auto-open the Appointments panel so the
  // user sees their connected status. Then clean location.state so a simple
  // re-render doesn't re-show the same toast.
  useEffect(() => {
    const st = location.state || {};
    const gcalResult = st.gcalResult;
    const gcalError = st.gcalError;
    if (!gcalResult && !gcalError) return;

    if (gcalResult?.success && gcalResult?.linked) {
      const email = gcalResult.googleEmail;
      setNotification({
        message: email
          ? `Google Calendar connected as ${email}`
          : "Google Calendar connected!",
        type: "success",
      });
    } else if (gcalError?.success === false) {
      setNotification({
        message: gcalError.message || "Failed to connect Google Calendar.",
        type: "error",
      });
    }

    // Clear the one-time flags from history state so navigating back or
    // remounting UserHome doesn't re-trigger the toast.
    try {
      const cleaned = { ...st };
      delete cleaned.gcalResult;
      delete cleaned.gcalError;
      window.history.replaceState(
        { ...window.history.state, usr: cleaned, key: window.history.state?.key, idx: window.history.state?.idx },
        document.title
      );
    } catch (_) {
      // best effort — ignore if replaceState fails for any reason
    }
  }, [location.state]);

  const handleProfileClick = () => {
    if (!userData) {
      navigate("/login");
      return;
    }

    // Forward the data to the profile
    navigate("/user-profile", { state: { user: userData } });
  };

  const handleCloseModal = () => {
    setShowPersonalizationModal(false);
    // Clear the fromSignup flag from location state to prevent modal from showing again
    try {
      const st = location.state || {};
      if (st.fromSignup) {
        const cleaned = { ...st };
        delete cleaned.fromSignup;
        window.history.replaceState(
          { ...window.history.state, usr: cleaned, key: window.history.state?.key, idx: window.history.state?.idx },
          document.title
        );
      }
    } catch (_) {
      // best effort — ignore if replaceState fails for any reason
    }
  };

   const [activeTab, setActiveTab] = useState(() => {
    // Check if we should open a specific tab from localStorage
    const pendingTab = localStorage.getItem('pendingTab');
    if (pendingTab) {
      localStorage.removeItem('pendingTab');
      return pendingTab;
    }
    // Check if we should open profile panel from navigation state
    if (location.state?.openProfilePanel) {
      return "profile";
    }
    // Redirect back from Google OAuth callback -> open the Appointments tab
    // so the user lands in the panel they were working in.
    if (location.state?.openAppointmentTab) {
      return "appointment";
    }
    return "learn";
  });

  // Check for pendingTab on location changes (handles same-route navigation)
  useEffect(() => {
    const pendingTab = localStorage.getItem('pendingTab');
    if (pendingTab) {
      localStorage.removeItem('pendingTab');
      setActiveTab(pendingTab);
    }
  }, [location]);

  const renderPanel = () => {
    switch (activeTab) {
      case "askai":
        return <AskAi />;

      case "records":
        return <RecordsPanel />;

      case "appointment":
        return <AppointmentPanel />;

      case "notifications":
        return <Notifications setActiveTab={setActiveTab} />;

      case "profile":
        return (
          <UserProfile
            initialItem={
              location.state?.activePanel === "privacy" ||
              location.state?.activePanel === "accesshistory"
                ? "privacy"
                : "account"
            }
            initialPrivacyTab={
              location.state?.activePrivacyTab ||
              (location.state?.activePanel === "accesshistory"
                ? "history"
                : "pending")
            }
          />
        );

      case "learn":
      default:
        return <Learn />;
    }
  };

  return (
    <div className="user-home">
      {showPersonalizationModal && (
        <ContentPersonalizationModal onClose={handleCloseModal} />
      )}
      <NotificationModal
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
     <UserTopNavBar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="main-panel">
        {renderPanel()}
      </main>
    </div>
  );
}

export default UserHome;
